import { setConfig } from '@leagueplatform/core';
import {
  useAuthoringEnvironments,
  useFetchTenantConfig,
  useConfigAppParams,
} from '@web-config-app/core-react';
import { useEffect, useState } from 'react';

/**
 * Hook that fetches tenant-specific configuration data and updates the global application config.
 *
 * Retrieves locale information from the tenant API (for now) and merges it into the global config state,
 * including setting the default locale and transforming locale data for the application.
 * Only fetches data when the user is authenticated and updates loading state accordingly.
 *
 * @param tenantId - Unique identifier for the tenant
 * @param isAuthenticated - Whether the user is currently authenticated
 * @returns Object with loading and error states
 * @returns returns.isTenantConfigLoading - True when fetching tenant data and updating config
 * @returns returns.isTenantConfigError - True when fetch fails
 */
export const useUpdateTenantConfig = ({
  isAuthenticated,
  tenantId,
}: {
  isAuthenticated?: boolean;
  tenantId: string;
}) => {
  const [isTenantConfigLoading, setIsTenantConfigLoading] = useState(true);

  // Get current environment from URL parameters
  const { environmentKey } = useConfigAppParams();

  const { currentAuthoringEnvironment } = useAuthoringEnvironments({
    environmentKey,
  });

  const {
    data: tenantData,
    isError: isTenantConfigError,
    isLoading,
  } = useFetchTenantConfig({
    environmentKey: currentAuthoringEnvironment,
    tenantId,
    isAuthenticated,
  });

  useEffect(() => {
    if (isAuthenticated && tenantData?.attributes?.locales) {
      const transformedLocales = tenantData?.attributes?.locales?.map(
        (locale) => ({
          code: locale.code,
          isDefault: locale.isDefault,
          label: { text: locale.name },
        }),
      );

      const defaultLocale = tenantData.attributes.locales.find(
        (locale) => locale.isDefault,
      );

      setConfig((draft) => {
        const updatedConfigApp = {
          ...(draft.configApp ?? {}),
          locales: transformedLocales,
          defaultLocale: defaultLocale?.code,
        };
        // eslint-disable-next-line no-param-reassign
        draft.configApp = updatedConfigApp;
      });
      setIsTenantConfigLoading(false);
    } else {
      setIsTenantConfigLoading(isLoading);
    }
  }, [
    isAuthenticated,
    isLoading,
    tenantData?.attributes?.locales,
    currentAuthoringEnvironment,
  ]);

  return {
    isTenantConfigLoading,
    isTenantConfigError,
  };
};
